import {
  CREATOR_APPLY_PATH,
  CREATOR_PATHS,
  isCreatorStudioPath,
  resolveCreatorStudioPath,
} from './creatorProgramPaths';
import { IUser, UserRole } from '../types/auth.types';
import { hasStaffPortalRole } from '../utils/staffPortalAccess';
import { hasCreatorPortalRole } from '../utils/creatorPortalAccess';

export type WorkspaceId = 'buyer' | 'seller' | 'creator' | 'owner' | 'staff';

export type WorkspaceLink = {
  id: WorkspaceId;
  label: string;
  description: string;
  path: string;
  /** Tailwind gradient for chip / accent */
  accentClass: string;
};

const SELLER_WS: WorkspaceLink = {
  id: 'seller',
  label: 'Seller Dashboard',
  description: 'Design, list & fulfill',
  path: '/seller/dashboard',
  accentClass: 'from-violet-600 to-purple-600',
};

const CREATOR_WS: WorkspaceLink = {
  id: 'creator',
  label: 'Creator Studio',
  description: 'Promote & earn commissions',
  path: CREATOR_PATHS.dashboard,
  accentClass: 'from-fuchsia-600 to-pink-600',
};

const BUYER_WS: WorkspaceLink = {
  id: 'buyer',
  label: 'Account',
  description: 'Orders, designs & wishlist',
  path: '/account',
  accentClass: 'from-[#3B3B98] to-[#9B59B6]',
};

const OWNER_WS: WorkspaceLink = {
  id: 'owner',
  label: 'Command center',
  description: 'Platform operations',
  path: '/owner',
  accentClass: 'from-indigo-700 to-violet-600',
};

export function getUserWorkspaces(
  user: IUser | null | undefined,
  options?: { creatorOnboardingComplete?: boolean | null },
): WorkspaceLink[] {
  if (!user?.roles?.length) return [];

  const roles = user.roles;
  const out: WorkspaceLink[] = [];

  if (roles.includes(UserRole.OWNER)) {
    out.push(OWNER_WS);
  } else if (hasStaffPortalRole(roles)) {
    out.push({ ...OWNER_WS, id: 'staff', label: 'Operations hub' });
  }

  if (roles.includes(UserRole.SELLER)) {
    out.push(SELLER_WS);
  }

  if (hasCreatorPortalRole(roles)) {
    out.push({
      ...CREATOR_WS,
      path: resolveCreatorStudioPath(options?.creatorOnboardingComplete),
    });
  }

  out.push(BUYER_WS);

  return out;
}

export function getCurrentWorkspaceId(pathname: string): WorkspaceId {
  if (pathname.startsWith('/seller')) return 'seller';
  if (isCreatorStudioPath(pathname)) return 'creator';
  if (pathname.startsWith('/owner')) return 'owner';
  return 'buyer';
}

export function isWorkspacePathMatch(ws: WorkspaceLink, pathname: string): boolean {
  if (ws.id === 'creator' && isCreatorStudioPath(pathname)) return true;
  if (ws.id === 'seller' && pathname.startsWith('/seller')) return true;
  if (ws.id === 'owner' && pathname.startsWith('/owner')) return true;
  if (ws.id === 'buyer' && pathname.startsWith('/account')) return true;
  return pathname === ws.path || pathname.startsWith(`${ws.path}/`);
}

export function findActiveWorkspace(
  workspaces: WorkspaceLink[],
  pathname: string,
): WorkspaceLink | undefined {
  return workspaces.find((ws) => isWorkspacePathMatch(ws, pathname));
}

/** Hide workspace that matches the page the user is already on. */
export function getAlternateWorkspaces(
  user: IUser,
  pathname: string,
): WorkspaceLink[] {
  return getUserWorkspaces(user).filter((ws) => {
    if (ws.id === 'seller' && pathname.startsWith('/seller')) return false;
    if (ws.id === 'creator' && isCreatorStudioPath(pathname)) return false;
    if ((ws.id === 'owner' || ws.id === 'staff') && pathname.startsWith('/owner')) return false;
    if (ws.id === 'buyer' && pathname.startsWith('/account')) return false;
    return true;
  });
}

export function getCreatorApplyPath(isAuthenticated: boolean): string {
  return isAuthenticated ? CREATOR_APPLY_PATH : '/login';
}
